import classNames from 'classnames'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer'
import useCountUp from '../../../hooks/useCountUp'
import { GET_TRANSACTIONS_CUSTOMER_DATA_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries'
import { TransactionLight, TransactionsCustomerDataResponse } from '../../../integrations/allkort/graphql/interface/Customer'
import { formatSEK } from '../../../utils/formatSEK'
import { AnalyticsIcon, InfoIconOutlined, SingleCardIcon } from '../../svgs/ExpressonSVGs'
import FetchingAnimation from '../FetchingAnimation'
import Tooltip from '../Tooltip'

type FuelStatsProps = {
    isCompany?: boolean
    hasCreditcard?: boolean
    hasSmartcard?: boolean
    hasBankcard?: boolean
}

const getTransactionKey = (transaction: TransactionLight) =>
    `${transaction.at}-${transaction.cardNumber}-${transaction.amount}`

const calculateTransactionTotals = (transactions: TransactionLight[]) =>
    transactions.reduce((totals, transaction) => {
        totals.amount += transaction.amount || 0
        totals.volume += transaction.quantity || 0
        return totals
    }, { amount: 0, volume: 0 })

type FetchTiming = {
    startedAt: number | null
    batches: number
    receivedNodes: number
    minNodesPerBatch: number | null
    maxNodesPerBatch: number
    completed: boolean
}

type TransactionPageInfo = {
    startCursor?: string | null
    endCursor?: string | null
    hasPreviousPage?: boolean
}

let nextFuelStatsRunId = 0

const createFetchTiming = (): FetchTiming => ({
    startedAt: null,
    batches: 0,
    receivedNodes: 0,
    minNodesPerBatch: null,
    maxNodesPerBatch: 0,
    completed: false
})

const fingerprintCursor = (cursor: string | null | undefined) => {
    if (!cursor) return null

    let hash = 2166136261
    for (let index = 0; index < cursor.length; index += 1) {
        hash = Math.imul(hash ^ cursor.charCodeAt(index), 16777619)
    }

    return (hash >>> 0).toString(16).padStart(8, '0')
}

const recordFetchBatch = (
    timing: FetchTiming,
    label: string,
    runId: number,
    requestedBefore: string | null,
    requestedLast: number,
    pageInfo: TransactionPageInfo | null | undefined,
    nodeCount: number
) => {
    timing.batches += 1
    timing.receivedNodes += nodeCount
    timing.minNodesPerBatch = timing.minNodesPerBatch === null
        ? nodeCount
        : Math.min(timing.minNodesPerBatch, nodeCount)
    timing.maxNodesPerBatch = Math.max(timing.maxNodesPerBatch, nodeCount)

    console.log(`[FuelStats:${runId}] ${label} transaction batch`, {
        batch: timing.batches,
        requestedBefore: fingerprintCursor(requestedBefore),
        requestedLast,
        startCursor: fingerprintCursor(pageInfo?.startCursor),
        endCursor: fingerprintCursor(pageInfo?.endCursor),
        receivedNodes: nodeCount,
        hasPreviousPage: pageInfo?.hasPreviousPage ?? false
    })
}

const logFetchStart = (timing: FetchTiming, label: string, runId: number) => {
    if (timing.startedAt !== null) return

    timing.startedAt = performance.now()
    console.log(`[FuelStats:${runId}] ${label} transaction fetch started`)
}

const logFetchCompletion = (timing: FetchTiming, label: string, runId: number, transactionCount: number, hasError: boolean) => {
    if (timing.startedAt === null || timing.completed) return

    timing.completed = true
    console.log(`[FuelStats:${runId}] ${label} transaction fetch finished`, {
        durationMs: Math.round(performance.now() - timing.startedAt),
        batches: timing.batches,
        receivedNodes: timing.receivedNodes,
        minNodesPerBatch: timing.minNodesPerBatch,
        maxNodesPerBatch: timing.maxNodesPerBatch,
        transactions: transactionCount,
        hasError
    })
}

const FuelStats: React.FC<FuelStatsProps> = ({ isCompany, hasCreditcard, hasSmartcard, hasBankcard }) => {
    const [fetchRunId] = useState(() => ++nextFuelStatsRunId)

    // Track whether component is mounted to prevent state updates after unmount (React Router transition abort)
    const isMountedRef = useRef(true);

    useEffect(() => {
        // Reset on (re)mount: StrictMode's dev double-invoke runs cleanup then setup again,
        // so this must flip the flag back to true or it stays permanently false after the
        // first simulated unmount, silently blocking every later state update.
        isMountedRef.current = true;
        return () => {
            // Cleanup on unmount: mark component as unmounted so in-flight effects don't update state
            isMountedRef.current = false;
        };
    }, []);

    // State to accumulate all fetched transactions for this month
    const [allTransactions, setAllTransactions] = useState<TransactionLight[]>([]);
    const [currentBefore, setCurrentBefore] = useState<string | null>(null);
    const [isBatchLoading, setIsBatchLoading] = useState(true);
    const [lastProcessedCursor, setLastProcessedCursor] = useState<string | null>(null);
    const monthlyFetchTimingRef = useRef<FetchTiming>(createFetchTiming());

    // State for billing period transactions by card type
    const [cardTypeTransactions, setCardTypeTransactions] = useState<{
        P: TransactionLight[];
        K: TransactionLight[];
    }>({ P: [], K: [] });
    const [currentBeforeBilling, setCurrentBeforeBilling] = useState<string | null>(null);
    const [isBatchLoadingBilling, setIsBatchLoadingBilling] = useState(true);
    const [lastProcessedCursorBilling, setLastProcessedCursorBilling] = useState<string | null>(null);
    const billingFetchTimingRef = useRef<FetchTiming>(createFetchTiming());
    const [bankcardTransactions, setBankcardTransactions] = useState<TransactionLight[]>([]);
    const [currentBeforeBankcard, setCurrentBeforeBankcard] = useState<string | null>(null);
    const [isBatchLoadingBankcard, setIsBatchLoadingBankcard] = useState(true);
    const [lastProcessedCursorBankcard, setLastProcessedCursorBankcard] = useState<string | null>(null);
    const bankcardFetchTimingRef = useRef<FetchTiming>(createFetchTiming());
    // Moderate page sizes reduce serial cursor round trips while keeping query cost bounded.
    const BATCH_SIZE = 20;

    // Calculate date range for current month (from start of month to today)
    const dateRange = useMemo(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        startOfMonth.setHours(0, 0, 0, 0);

        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);

        return { start: startOfMonth, end: endOfToday };
    }, []);

    // Calculate full calendar month (1st to last day) for bankcard
    const calendarMonth = useMemo(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        startOfMonth.setHours(0, 0, 0, 0);

        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);

        const shortMonths = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
        const formatDate = (date: Date) =>
            `${date.getDate()} ${shortMonths[date.getMonth()]}`;

        return {
            start: startOfMonth,
            end: endOfMonth,
            formatted: `${formatDate(startOfMonth)} - ${formatDate(endOfMonth)}`
        };
    }, []);

    // Calculate billing period (16th of one month to 15th of next)
    const billingPeriod = useMemo(() => {
        const today = new Date();
        const currentDay = today.getDate();

        let startDate: Date;
        let endDate: Date;

        if (currentDay >= 16) {
            // Period is from 16th of this month to 15th of next month
            startDate = new Date(today.getFullYear(), today.getMonth(), 16);
            endDate = new Date(today.getFullYear(), today.getMonth() + 1, 15);
        } else {
            // Period is from 16th of previous month to 15th of this month
            startDate = new Date(today.getFullYear(), today.getMonth() - 1, 16);
            endDate = new Date(today.getFullYear(), today.getMonth(), 15);
        }

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        const shortMonths = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

        const formatDate = (date: Date) =>
            `${date.getDate()} ${shortMonths[date.getMonth()]}`;

        return {
            start: startDate,
            end: endDate,
            formatted: `${formatDate(startDate)} - ${formatDate(endDate)}`
        };
    }, []);

    // Build whereClause for current month's transactions
    const whereClause = useMemo(() => {
        const where: any = {
            quantity: { gt: 0 } // Filter out zero-quantity transactions
        };

        // Get timezone offset in milliseconds
        const tzOffset = dateRange.start.getTimezoneOffset() * 60 * 1000;

        // Add date range filter for current month
        where.at = {
            gt: new Date(dateRange.start.getTime() - tzOffset).toISOString(),
            lt: new Date(dateRange.end.getTime() - tzOffset).toISOString()
        };

        return where;
    }, [dateRange]);

    // Build whereClause for member-card transactions in the 16th-to-15th period.
    const whereClauseBilling = useMemo(() => {
        const transactionTypes: string[] = [];
        if (hasSmartcard) transactionTypes.push('P');
        if (hasCreditcard) transactionTypes.push('K');

        const where: any = {
            quantity: { gt: 0 }, // Filter out zero-quantity transactions
            ...(transactionTypes.length > 0 && { transactionType: { in: transactionTypes } })
        };

        // Get timezone offset in milliseconds
        const tzOffset = billingPeriod.start.getTimezoneOffset() * 60 * 1000;

        // Add date range filter for the current member-card billing period.
        where.at = {
            gt: new Date(billingPeriod.start.getTime() - tzOffset).toISOString(),
            lt: new Date(billingPeriod.end.getTime() - tzOffset).toISOString()
        };

        return where;
    }, [billingPeriod, hasCreditcard, hasSmartcard]);

    // Bankcard transactions use the calendar month rather than the member-card billing period.
    const whereClauseBankcard = useMemo(() => ({
        quantity: { gt: 0 },
        transactionType: { eq: 'B' },
        at: {
            gt: new Date(calendarMonth.start.getTime() - calendarMonth.start.getTimezoneOffset() * 60 * 1000).toISOString(),
            lt: new Date(calendarMonth.end.getTime() - calendarMonth.end.getTimezoneOffset() * 60 * 1000).toISOString()
        }
    }), [calendarMonth]);

    // Query variables for fetching transactions
    const queryVariables = useMemo(() => ({
        before: currentBefore,
        last: BATCH_SIZE,
        where: whereClause
    }), [currentBefore, whereClause]);

    // Query variables for billing period transactions
    const queryVariablesBilling = useMemo(() => ({
        before: currentBeforeBilling,
        last: BATCH_SIZE,
        where: whereClauseBilling
    }), [currentBeforeBilling, whereClauseBilling]);

    const queryVariablesBankcard = useMemo(() => ({
        before: currentBeforeBankcard,
        last: BATCH_SIZE,
        where: whereClauseBankcard
    }), [currentBeforeBankcard, whereClauseBankcard]);

    // Fetch transactions
    const { data: transactionsData, loading: isLoading, error: transactionsError } = useGetCustomer<TransactionsCustomerDataResponse>(
        GET_TRANSACTIONS_CUSTOMER_DATA_QUERY,
        queryVariables,
        { fetchPolicy: 'cache-first' }
    );

    // Fetch billing period transactions
    const { data: transactionsDataBilling, loading: isLoadingBilling, error: billingError } = useGetCustomer<TransactionsCustomerDataResponse>(
        GET_TRANSACTIONS_CUSTOMER_DATA_QUERY,
        queryVariablesBilling,
        { fetchPolicy: 'cache-first', skip: !hasCreditcard && !hasSmartcard }
    );

    const { data: transactionsDataBankcard, loading: isLoadingBankcard, error: bankcardError } = useGetCustomer<TransactionsCustomerDataResponse>(
        GET_TRANSACTIONS_CUSTOMER_DATA_QUERY,
        queryVariablesBankcard,
        { fetchPolicy: 'cache-first', skip: !hasBankcard }
    );

    useEffect(() => {
        logFetchStart(monthlyFetchTimingRef.current, 'Monthly totals', fetchRunId)
        if (hasCreditcard || hasSmartcard) {
            logFetchStart(billingFetchTimingRef.current, 'Member-card details', fetchRunId)
        }
        if (hasBankcard) {
            logFetchStart(bankcardFetchTimingRef.current, 'Bank-card details', fetchRunId)
        }
    }, [hasCreditcard, hasSmartcard, hasBankcard, fetchRunId]);

    useEffect(() => {
        if (!hasCreditcard && !hasSmartcard) {
            setIsBatchLoadingBilling(false);
        }
        if (!hasBankcard) {
            setIsBatchLoadingBankcard(false);
        }
    }, [hasCreditcard, hasSmartcard, hasBankcard]);

    useEffect(() => {
        if (!isLoading && !transactionsData?.currentCustomer?.transactions) {
            setIsBatchLoading(false);
        }
    }, [isLoading, transactionsData]);

    useEffect(() => {
        if (!isLoadingBilling && !transactionsDataBilling?.currentCustomer?.transactions) {
            setIsBatchLoadingBilling(false);
        }
    }, [isLoadingBilling, transactionsDataBilling]);

    useEffect(() => {
        if (!isLoadingBankcard && !transactionsDataBankcard?.currentCustomer?.transactions) {
            setIsBatchLoadingBankcard(false);
        }
    }, [isLoadingBankcard, transactionsDataBankcard]);

    // Process fetched data when it arrives
    useEffect(() => {
        if (!isLoading && transactionsData?.currentCustomer?.transactions) {
            if (!isMountedRef.current) return; // Prevent state updates after unmount

            const { pageInfo, nodes } = transactionsData.currentCustomer.transactions;

            if (nodes && nodes.length > 0) {
                const currentCursor = pageInfo?.startCursor;

                // Skip if already processed this cursor
                if (lastProcessedCursor === currentCursor) return;
                recordFetchBatch(monthlyFetchTimingRef.current, 'Monthly totals', fetchRunId, currentBefore, BATCH_SIZE, pageInfo, nodes.length);

                // Add new transactions
                setAllTransactions(prev => {
                    const existingIds = new Set(prev.map(getTransactionKey));
                    const newTransactions = nodes.filter(t => !existingIds.has(getTransactionKey(t)));
                    return [...prev, ...newTransactions];
                });

                setLastProcessedCursor(currentCursor);

                // Check if more pages exist
                if (pageInfo?.hasPreviousPage && pageInfo?.endCursor) {
                    // Continue fetching
                    setCurrentBefore(pageInfo.endCursor);
                } else {
                    // No more pages - done loading
                    setIsBatchLoading(false);
                }
            } else {
                // No transactions returned - done
                recordFetchBatch(monthlyFetchTimingRef.current, 'Monthly totals', fetchRunId, currentBefore, BATCH_SIZE, pageInfo, nodes?.length ?? 0);
                setIsBatchLoading(false);
            }
        }
    }, [transactionsData, isLoading, lastProcessedCursor, currentBefore, allTransactions.length]);

    // Process billing period transactions
    useEffect(() => {
        if (!isLoadingBilling && transactionsDataBilling?.currentCustomer?.transactions) {
            if (!isMountedRef.current) return; // Prevent state updates after unmount

            const { pageInfo, nodes } = transactionsDataBilling.currentCustomer.transactions;

            if (nodes && nodes.length > 0) {
                const currentCursor = pageInfo?.startCursor;

                // Skip if already processed this cursor
                if (lastProcessedCursorBilling === currentCursor) return;
                recordFetchBatch(billingFetchTimingRef.current, 'Member-card details', fetchRunId, currentBeforeBilling, BATCH_SIZE, pageInfo, nodes.length);

                // Separate by card type (P = smartcard, K = creditcard/tankkort, B = bankcard)
                setCardTypeTransactions(prev => {
                    const existingIdsP = new Set(prev.P.map(getTransactionKey));
                    const existingIdsK = new Set(prev.K.map(getTransactionKey));

                    const smartcardTx = nodes.filter(t => t.transactionType === 'P' && !existingIdsP.has(getTransactionKey(t)));
                    const creditcardTx = nodes.filter(t => t.transactionType === 'K' && !existingIdsK.has(getTransactionKey(t)));

                    return {
                        P: [...prev.P, ...smartcardTx],
                        K: [...prev.K, ...creditcardTx]
                    };
                });

                setLastProcessedCursorBilling(currentCursor);

                // Check if more pages exist
                if (pageInfo?.hasPreviousPage && pageInfo?.endCursor) {
                    // Continue fetching
                    setCurrentBeforeBilling(pageInfo.endCursor);
                } else {
                    // No more pages - done loading
                    setIsBatchLoadingBilling(false);
                }
            } else {
                recordFetchBatch(billingFetchTimingRef.current, 'Member-card details', fetchRunId, currentBeforeBilling, BATCH_SIZE, pageInfo, nodes?.length ?? 0);
                // No transactions returned - done
                setIsBatchLoadingBilling(false);
            }
        }
    }, [transactionsDataBilling, isLoadingBilling, lastProcessedCursorBilling, currentBeforeBilling]);

    // Process calendar-month bankcard transactions separately from member-card billing data.
    useEffect(() => {
        if (!isLoadingBankcard && transactionsDataBankcard?.currentCustomer?.transactions) {
            if (!isMountedRef.current) return;

            const { pageInfo, nodes } = transactionsDataBankcard.currentCustomer.transactions;

            if (nodes && nodes.length > 0) {
                const currentCursor = pageInfo?.startCursor;

                if (lastProcessedCursorBankcard === currentCursor) return;
                recordFetchBatch(bankcardFetchTimingRef.current, 'Bank-card details', fetchRunId, currentBeforeBankcard, BATCH_SIZE, pageInfo, nodes.length);

                setBankcardTransactions(prev => {
                    const existingIds = new Set(prev.map(getTransactionKey));
                    return [...prev, ...nodes.filter(t => !existingIds.has(getTransactionKey(t)))];
                });
                setLastProcessedCursorBankcard(currentCursor);

                if (pageInfo?.hasPreviousPage && pageInfo?.endCursor) {
                    setCurrentBeforeBankcard(pageInfo.endCursor);
                } else {
                    setIsBatchLoadingBankcard(false);
                }
            } else {
                recordFetchBatch(bankcardFetchTimingRef.current, 'Bank-card details', fetchRunId, currentBeforeBankcard, BATCH_SIZE, pageInfo, nodes?.length ?? 0);
                setIsBatchLoadingBankcard(false);
            }
        }
    }, [transactionsDataBankcard, isLoadingBankcard, lastProcessedCursorBankcard, currentBeforeBankcard, bankcardTransactions.length]);

    useEffect(() => {
        if (!isBatchLoading) {
            logFetchCompletion(monthlyFetchTimingRef.current, 'Monthly totals', fetchRunId, allTransactions.length, Boolean(transactionsError));
        }
    }, [isBatchLoading, allTransactions.length, transactionsError, fetchRunId]);

    useEffect(() => {
        if (!isBatchLoadingBilling) {
            logFetchCompletion(
                billingFetchTimingRef.current,
                'Member-card details',
                fetchRunId,
                cardTypeTransactions.P.length + cardTypeTransactions.K.length,
                Boolean(billingError)
            );
        }
    }, [isBatchLoadingBilling, cardTypeTransactions.P.length, cardTypeTransactions.K.length, billingError, fetchRunId]);

    useEffect(() => {
        if (!isBatchLoadingBankcard) {
            logFetchCompletion(
                bankcardFetchTimingRef.current,
                'Bank-card details',
                fetchRunId,
                bankcardTransactions.length,
                Boolean(bankcardError)
            );
        }
    }, [isBatchLoadingBankcard, bankcardTransactions.length, bankcardError, fetchRunId]);

    // Calculate total volume (liters) and total cost from all transactions
    const { volume: totalVolume, amount: totalCost } = useMemo(
        () => calculateTransactionTotals(allTransactions),
        [allTransactions]
    );
    const averagePricePerLiter = totalVolume > 0 ? totalCost / totalVolume : 0;

    // Calculate totals for each card type during billing period
    const cardTypeStats = useMemo(() => ({
        smartcard: calculateTransactionTotals(cardTypeTransactions.P),
        creditcard: calculateTransactionTotals(cardTypeTransactions.K),
        bankcard: calculateTransactionTotals(bankcardTransactions)
    }), [cardTypeTransactions, bankcardTransactions]);

    // Animated display values — count up from 0 once loading is complete
    const countUpDuration = totalCost > 2500 || totalVolume > 150 ? 1500 : 3000;
    const animatedVolume = useCountUp(totalVolume, countUpDuration, !isBatchLoading);
    const animatedCost = useCountUp(totalCost, countUpDuration, !isBatchLoading);
    const animatedAvgPrice = useCountUp(averagePricePerLiter, countUpDuration, !isBatchLoading);

    return (
        <div className="fuelstats_component">
            <h5 className="body-text">
                <AnalyticsIcon width={28} height={28} />
                {`Statistik`}
            </h5>
            <div className="fuelstats-content">
                <div className="single-fuelstat blue">
                    <label htmlFor="total-volume" className="with-tooltip">
                        {`Total volym ${dateRange.start.toLocaleDateString('sv-SE', { month: 'long' })}`}
                        <Tooltip position='top-end' text="Volymen är på alla dina tankade drivmedel denna månad.">
                            <InfoIconOutlined width={26} height={26} />
                        </Tooltip>
                    </label>
                    <span id="total-volume">
                        {isBatchLoading ? <FetchingAnimation width={100} height={30} marginTop={6} /> : `${animatedVolume.toFixed(2)} L`}
                        <span className="small-label">
                            {/* TODO: Calculate vs previous month */}
                            {!isBatchLoading && allTransactions.length > 0 ? `${allTransactions.length} transaktioner` : '0 transaktioner'}
                        </span>
                    </span>

                </div>

                <div className="single-fuelstat green">
                    <label htmlFor="total-cost" className="with-tooltip">
                        {`Total kostnad ${dateRange.start.toLocaleDateString('sv-SE', { month: 'long' })}`}
                        <Tooltip position="top-end" text="Detta är en preliminär summa utan eventuella avdrag.">
                            <InfoIconOutlined width={25} height={26} />
                        </Tooltip>
                    </label>
                    <span id="total-cost">
                        {isBatchLoading ? <FetchingAnimation width={145} height={30} marginTop={6} /> : formatSEK(animatedCost).replace(',', '.')}
                        <span className="small-label">
                            {/* TODO: Calculate vs previous month */}
                            {/* TODO: this is hidden now{!isBatchLoading && dateRange.start ? `Från ${dateRange.start.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}` : ''}*/}
                            {`Alla kort i ${dateRange.start.toLocaleDateString('sv-SE', { month: 'long' })}`}
                        </span>
                    </span>

                </div>
                {hasBankcard || hasCreditcard || hasSmartcard ? (
                    <div className={classNames(
                        "single-fuelstats-advanced",
                        "yellow",
                        "specified",
                        {
                            'no-bankcard': !hasBankcard,
                            'no-membercards': !hasCreditcard && !hasSmartcard
                        })}>
                        <label htmlFor="average-price-per-liter" className="with-tooltip">
                            Detaljer per betalmedel
                            <Tooltip position="top-end" text="Olika korttyper har olika betalperioder. Nedan visas statistik för den aktuella betalperioden och kort.">
                                <InfoIconOutlined width={25} height={26} />
                            </Tooltip>
                        </label>
                        <div className="advanced-details">
                            {hasSmartcard && (
                                <span className="smartcard">
                                    <label>
                                        <SingleCardIcon className={classNames('service-icon', 'blue'
                                        )} width={24} height={24} />

                                        Smartkort, {billingPeriod.formatted}
                                    </label>
                                    {isBatchLoadingBilling ? <FetchingAnimation width={100} height={20} marginTop={2} /> : `${formatSEK(cardTypeStats.smartcard.amount).replace(',', '.')} och ${cardTypeStats.smartcard.volume.toFixed(2)}L`}
                                </span>
                            )}

                            {hasCreditcard && (
                                <span className="creditcard">
                                    <label>
                                        <SingleCardIcon className={classNames('service-icon',
                                            {
                                                'grey': isCompany,
                                                'red': !isCompany,
                                            }
                                        )} width={24} height={24} />
                                        {isCompany ? 'Företagskort,' : 'Tankkort,'} {billingPeriod.formatted}
                                    </label>
                                    {isBatchLoadingBilling ? <FetchingAnimation width={100} height={20} marginTop={2} /> : `${formatSEK(cardTypeStats.creditcard.amount).replace(',', '.')} och ${cardTypeStats.creditcard.volume.toFixed(2)}L`}
                                </span>
                            )}

                            {hasBankcard && (
                                <span className="bankcard">
                                    <label>
                                        <SingleCardIcon className={classNames('service-icon', 'black'
                                        )} width={24} height={24} />
                                        Bankkort, {calendarMonth.formatted}
                                    </label>
                                    {isBatchLoadingBankcard ? <FetchingAnimation width={100} height={20} marginTop={2} /> : `${formatSEK(cardTypeStats.bankcard.amount).replace(',', '.')} och ${cardTypeStats.bankcard.volume.toFixed(2)}L`}
                                </span>
                            )}
                        </div>
                    </div>
                ) : null}

            </div>
        </div>
    )
}
export default FuelStats;